import type { EffectiveStatus } from '@/hooks/useContentArtifacts';
import type { SchedulePublishState } from '@/spaces/chat/panels/scheduleUtils';

export type StatusVariant =
	| 'informative'
	| 'neutral'
	| 'negative'
	| 'notice'
	| 'positive';

// `publishState` is not a status: the status union comes from the backend, and
// how a late post is doing is something only the caller holding the schedule and
// the Adobe Express resolution record knows.
export const getStatusVariant = (
	status: EffectiveStatus,
	publishState: SchedulePublishState = 'on_time',
): StatusVariant => {
	switch (status) {
		case 'scheduled':
			return publishState === 'past_due' ? 'notice' : 'informative';
		case 'published':
			return 'neutral';
		case 'failed':
			return 'negative';
		case 'paused':
			return 'neutral';
		case 'cancelled':
			return 'neutral';
		// "pending" is the same thing to a customer as "draft" — an item an agent
		// submitted for approval rather than one a campaign created.
		case 'draft':
		case 'pending':
		case 'processing':
		case 'proposed':
		default:
			return 'notice';
	}
};

export const getStatusLabel = (
	status: EffectiveStatus,
	publishState: SchedulePublishState = 'on_time',
): string => {
	switch (status) {
		case 'scheduled':
			if (publishState === 'past_due') return 'Past due';
			return publishState === 'unconfirmed' ? 'Confirming' : 'Scheduled';
		case 'published':
			return 'Published';
		case 'draft':
		case 'pending':
			return 'Draft';
		case 'processing':
			return 'Processing';
		case 'failed':
			return 'Failed';
		case 'cancelled':
			return 'Cancelled';
		case 'paused':
			return 'Paused';
		case 'proposed':
			return 'Proposed';
		default:
			return '';
	}
};
